import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft, Bot, Loader2, Play, Trash2, Plus, MapPin, CheckCircle2,
  Sparkles, Activity, Eye, RefreshCw, X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_STYLE = {
  created: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  enriched: 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30',
  discovery: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30',
  run_started: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  run_finished: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  run_error: 'bg-red-500/15 text-red-300 border-red-500/30',
  city_launched: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
};

function StatTile({ label, value, accent }) {
  return (
    <Card className="border-border/60">
      <CardContent className="p-4">
        <p className={`text-2xl font-bold font-heading ${accent || ''}`}>{value}</p>
        <p className="text-[11px] text-muted-foreground mt-0.5">{label}</p>
      </CardContent>
    </Card>
  );
}

function LaunchCityForm({ onLaunched, onClose }) {
  const [form, setForm] = useState({
    city: '', state: '', categories_csv: 'wine_bar, cocktail_bar, bar, night_club, live_music_venue, event_venue, winery, brewery',
    radius_km: 15, min_rating: 4.0, min_reviews: 20,
  });
  const [submitting, setSubmitting] = useState(false);
  const headers = { Authorization: `Bearer ${localStorage.getItem('eventism_token')}` };

  const submit = async () => {
    if (!form.city.trim()) return toast.error('City name is required');
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/bot/cities`, {
        city: form.city.trim(),
        state: form.state.trim() || undefined,
        categories: form.categories_csv.split(',').map((s) => s.trim()).filter(Boolean),
        radius_km: Number(form.radius_km) || 15,
        min_rating: Number(form.min_rating) || 4.0,
        min_reviews: Number(form.min_reviews) || 20,
      }, { headers });
      toast.success(`${r.data.city.city} launched on autopilot — EventismBot will sweep it automatically. Hit "Run now" to start immediately.`);
      onLaunched();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Launch failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border-fuchsia-500/30 bg-gradient-to-br from-fuchsia-500/5 via-transparent to-transparent mb-6" data-testid="bot-launch-form">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between">
          <h2 className="text-base font-bold flex items-center gap-2">
            <MapPin className="h-4 w-4 text-fuchsia-300" /> Launch a city
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">City</Label>
            <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Austin" data-testid="bot-city-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">State (helps geocoding)</Label>
            <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="TX" data-testid="bot-state-input" />
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Venue categories to hunt (comma separated — Google Business categories)</Label>
          <Input value={form.categories_csv} onChange={(e) => setForm({ ...form, categories_csv: e.target.value })} data-testid="bot-categories-input" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Radius (km)</Label>
            <Input type="number" min={2} max={60} value={form.radius_km} onChange={(e) => setForm({ ...form, radius_km: e.target.value })} data-testid="bot-radius-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Min Google rating</Label>
            <Input type="number" step="0.1" min={0} max={5} value={form.min_rating} onChange={(e) => setForm({ ...form, min_rating: e.target.value })} data-testid="bot-minrating-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Min review count</Label>
            <Input type="number" min={0} value={form.min_reviews} onChange={(e) => setForm({ ...form, min_reviews: e.target.value })} data-testid="bot-minreviews-input" />
          </div>
        </div>
        <Button
          onClick={submit}
          disabled={submitting}
          className="bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 text-white border-0 hover:opacity-90"
          data-testid="bot-launch-submit"
        >
          {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Launching…</> : <><Bot className="h-4 w-4 mr-2" /> Launch autopilot</>}
        </Button>
      </CardContent>
    </Card>
  );
}

function CityCard({ c, onRun, onToggle, onDelete, running }) {
  const stats = c.last_run_stats;
  return (
    <Card className="border-border/60" data-testid={`bot-city-${c.city_id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="font-bold font-heading flex items-center gap-2">
              {c.city}{c.state ? <span className="text-muted-foreground font-normal text-xs">{c.state}</span> : null}
              {c.enabled
                ? <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px]">AUTOPILOT ON</Badge>
                : <Badge className="bg-zinc-500/15 text-zinc-300 border-zinc-500/30 text-[10px]">PAUSED</Badge>}
              {c.running && <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px] animate-pulse">SWEEPING…</Badge>}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {c.venue_count} venues · {c.perfect_count} perfect profiles · last run {c.last_run_at ? new Date(c.last_run_at).toLocaleString() : 'never'}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="outline" className="h-8 text-xs border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10"
              onClick={() => onRun(c)} disabled={running || c.running} data-testid={`bot-run-${c.city_id}`}>
              {c.running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1" />} Run now
            </Button>
            <Button size="sm" variant="outline" className="h-8 text-xs"
              onClick={() => onToggle(c)} data-testid={`bot-toggle-${c.city_id}`}>
              {c.enabled ? 'Pause' : 'Resume'}
            </Button>
            <button onClick={() => onDelete(c)} className="p-1.5 text-red-400 hover:text-red-300" data-testid={`bot-delete-${c.city_id}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
        {/* Coverage bar */}
        <div>
          <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
            <span>Profile completeness</span>
            <span className="font-semibold text-foreground">{c.avg_completeness}%</span>
          </div>
          <div className="h-2 rounded-full bg-border/50 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 transition-all"
              style={{ width: `${c.avg_completeness || 0}%` }}
            />
          </div>
        </div>
        {stats && (
          <p className="text-[11px] text-muted-foreground">
            Last sweep: <span className="text-emerald-300">{stats.created} added</span> · <span className="text-fuchsia-300">{stats.enriched} enriched</span> · {stats.discovered} discovered · ${Number(stats.cost || 0).toFixed(2)} spent
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminEventismBot() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLaunch, setShowLaunch] = useState(false);
  const [runningCity, setRunningCity] = useState(null);
  const headers = { Authorization: `Bearer ${localStorage.getItem('eventism_token')}` };

  const fetchOverview = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/bot/overview`, { headers });
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load EventismBot overview');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  // Auto-refresh while any city is sweeping so the activity feed is live.
  useEffect(() => {
    const anyRunning = (data?.cities || []).some((c) => c.running);
    if (!anyRunning) return undefined;
    const id = setInterval(() => fetchOverview(true), 5000);
    return () => clearInterval(id);
  }, [data, fetchOverview]);

  const runCity = async (c) => {
    setRunningCity(c.city_id);
    try {
      const r = await axios.post(`${API}/admin/bot/cities/${c.city_id}/run`, {}, { headers });
      toast.success(r.data?.message || 'Sweep started');
      fetchOverview(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Run failed');
    } finally {
      setRunningCity(null);
    }
  };

  const toggleCity = async (c) => {
    try {
      await axios.put(`${API}/admin/bot/cities/${c.city_id}`, { enabled: !c.enabled }, { headers });
      toast.success(c.enabled ? `${c.city} paused` : `${c.city} back on autopilot`);
      fetchOverview(true);
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const deleteCity = async (c) => {
    if (!window.confirm(`Remove ${c.city} from EventismBot? Existing venues stay.`)) return;
    try {
      await axios.delete(`${API}/admin/bot/cities/${c.city_id}`, { headers });
      toast.success(`${c.city} removed`);
      fetchOverview(true);
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const markChecked = async (v) => {
    try {
      await axios.post(`${API}/admin/bot/venues/${v.venue_id}/mark-checked`, {}, { headers });
      setData((d) => ({ ...d, needs_review: (d.needs_review || []).filter((x) => x.venue_id !== v.venue_id) }));
      toast.success(`${v.name} marked checked`);
    } catch (e) {
      toast.error('Failed');
    }
  };

  if (user?.role !== 'admin') return null;

  const totals = data?.totals || {};

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/venues')} data-testid="back-to-venues">
              <ArrowLeft className="h-4 w-4 mr-1" /> Venue Manager
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground flex items-center gap-2">
                <Bot className="h-6 w-6 text-fuchsia-400" /> EventismBot
              </h1>
              <p className="text-sm text-muted-foreground">
                Venue autopilot — discovers, enriches, and refreshes venue profiles per launched city. Google Business data via DataForSEO · photos via website scrape → CDN · descriptions via Eventism AI.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-9 text-xs" onClick={() => fetchOverview()} data-testid="bot-refresh">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
            </Button>
            <Button
              size="sm"
              className="h-9 text-xs bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 hover:opacity-90 text-white border-0 font-bold"
              onClick={() => setShowLaunch(true)}
              data-testid="bot-launch-city-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Launch City
            </Button>
          </div>
        </div>

        {loading && !data ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-fuchsia-400" /></div>
        ) : (
          <>
            {data && !data.configured && (
              <Card className="border-amber-500/40 bg-amber-500/5 mb-6">
                <CardContent className="p-4 text-sm text-amber-200">
                  DataForSEO credentials are not configured — set DATAFORSEO_LOGIN / DATAFORSEO_PASSWORD in the backend .env.
                </CardContent>
              </Card>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="bot-stats">
              <StatTile label="Cities on autopilot" value={totals.cities_enabled ?? 0} accent="text-sky-300" />
              <StatTile label="Venues added by bot" value={totals.total_created ?? 0} accent="text-emerald-300" />
              <StatTile label="Profiles enriched" value={totals.total_enriched ?? 0} accent="text-fuchsia-300" />
              <StatTile label="Total data spend" value={`$${Number(totals.total_cost || 0).toFixed(2)}`} accent="text-amber-300" />
            </div>

            {showLaunch && (
              <LaunchCityForm onLaunched={() => { setShowLaunch(false); fetchOverview(); }} onClose={() => setShowLaunch(false)} />
            )}

            {/* City cards */}
            <div className="space-y-3 mb-8" data-testid="bot-city-list">
              {(data?.cities || []).length === 0 && (
                <Card className="border-dashed border-border">
                  <CardContent className="p-8 text-center text-sm text-muted-foreground">
                    No cities on autopilot yet. Launch your first city — EventismBot will find every venue worth listing, build a perfect profile for each, and keep them fresh.
                  </CardContent>
                </Card>
              )}
              {(data?.cities || []).map((c) => (
                <CityCard key={c.city_id} c={c} onRun={runCity} onToggle={toggleCity} onDelete={deleteCity} running={runningCity === c.city_id} />
              ))}
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Needs review */}
              <div>
                <h2 className="text-sm font-bold mb-2 flex items-center gap-2">
                  <Eye className="h-4 w-4 text-amber-300" /> New venues to check
                  <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px]">{(data?.needs_review || []).length}</Badge>
                </h2>
                <Card className="border-border/60">
                  <CardContent className="p-0 divide-y divide-border/50 max-h-[420px] overflow-y-auto" data-testid="bot-needs-review">
                    {(data?.needs_review || []).length === 0 && (
                      <p className="p-4 text-xs text-muted-foreground">Nothing waiting — every bot-added venue has been checked.</p>
                    )}
                    {(data?.needs_review || []).map((v) => (
                      <div key={v.venue_id} className="flex items-center gap-3 p-3" data-testid={`bot-review-${v.venue_id}`}>
                        {(v.photos || [])[0]
                          ? <img src={v.photos[0]} alt={v.name} className="h-10 w-10 rounded-md object-cover" />
                          : <div className="h-10 w-10 rounded-md bg-border/50 flex items-center justify-center"><Sparkles className="h-4 w-4 text-muted-foreground" /></div>}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate">{v.name}</p>
                          <p className="text-[11px] text-muted-foreground">
                            {v.city} · {(v.venue_type || '').replace(/_/g, ' ')}{v.rating ? ` · ${v.rating}★` : ''}{v.completeness != null ? ` · ${v.completeness}% complete` : ''}
                          </p>
                        </div>
                        <a
                          href={`/events/${(v.city || '').toLowerCase()}/venues/${v.slug}`}
                          target="_blank" rel="noreferrer"
                          className="text-[11px] text-fuchsia-300 hover:underline shrink-0"
                        >
                          View
                        </a>
                        <Button size="sm" variant="outline" className="h-7 text-[11px] border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                          onClick={() => markChecked(v)} data-testid={`bot-check-${v.venue_id}`}>
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Checked
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>

              {/* Activity feed */}
              <div>
                <h2 className="text-sm font-bold mb-2 flex items-center gap-2">
                  <Activity className="h-4 w-4 text-fuchsia-300" /> Bot activity
                </h2>
                <Card className="border-border/60">
                  <CardContent className="p-0 divide-y divide-border/50 max-h-[420px] overflow-y-auto" data-testid="bot-activity-feed">
                    {(data?.activity || []).length === 0 && (
                      <p className="p-4 text-xs text-muted-foreground">No activity yet — launch a city and hit Run now.</p>
                    )}
                    {(data?.activity || []).map((a) => (
                      <div key={a.activity_id} className="flex items-start gap-2.5 p-3">
                        <Badge className={`text-[9px] shrink-0 mt-0.5 ${ACTION_STYLE[a.action] || 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30'}`}>
                          {a.action.replace(/_/g, ' ').toUpperCase()}
                        </Badge>
                        <div className="min-w-0">
                          <p className="text-xs">{a.venue_name ? <span className="font-semibold">{a.venue_name} — </span> : null}{a.detail}</p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">{a.city} · {new Date(a.ts).toLocaleString()}{a.cost ? ` · $${a.cost.toFixed(3)}` : ''}</p>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
